import { ArrowUpRight, Mic } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import Testimonials from "@/components/Testimonials";
import {
  RESOURCES_HERO_VIDEO,
  RESOURCES_HERO_IMAGE,
  TABERNACLE_IMAGE,
  HABITS_COVER_ARTWORK,
  COACHING_IMAGE,
  MANIFESTO_IMAGE,
  PODCASTS,
} from "@/content";

const ctaCls =
  "inline-flex items-center justify-center gap-3 bg-ink text-paper font-semibold text-xs tracking-[0.22em] uppercase px-8 py-4 rounded-full hover:bg-charcoal transition-colors duration-300";
const labelCls = "text-[11px] font-semibold tracking-[0.3em] uppercase text-fog";

function HabitsCover() {
  if (HABITS_COVER_ARTWORK) {
    return (
      <img
        src={HABITS_COVER_ARTWORK}
        alt="6 Healthy Habits of High-Level Worship Teams workbook cover"
        data-testid="habits-cover"
        className="mx-auto w-full max-w-md border border-ink/15 shadow-[10px_10px_0_rgba(9,9,9,0.12)]"
      />
    );
  }
  return (
    <div
      data-testid="habits-cover"
      className="relative mx-auto w-56 md:w-64 aspect-[3/4] bg-paper border border-ink/40 shadow-[10px_10px_0_rgba(9,9,9,0.15)] -rotate-2 flex flex-col justify-between p-6"
    >
      <div aria-hidden="true" className="absolute inset-y-0 left-0 w-[6px] bg-ink/15" />
      <p className="text-[9px] font-semibold tracking-[0.3em] uppercase text-fog pl-3">
        David Rosenblum
      </p>
      <p className="font-spartan font-extrabold uppercase tracking-tight leading-[1.08] text-[1.65rem] md:text-3xl pl-3">
        6 Healthy Habits of High-Level Worship Teams
      </p>
      <p className="text-[9px] font-semibold tracking-[0.3em] uppercase text-fog pl-3">
        People Over Platforms
      </p>
    </div>
  );
}

export default function Resources() {
  return (
    <section id="resources" data-testid="resources-section" className="py-24 md:py-36 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <SectionHeading number="06" title="Resources & Coaching" testId="resources-heading" />

        {/* ── 1 · Hero ─────────────────────────────────────── */}
        <Reveal className="mt-14 md:mt-20">
          <div data-testid="resources-hero" className="relative overflow-hidden min-h-[70vh] flex items-end bg-ink text-paper">
            {RESOURCES_HERO_VIDEO ? (
              <video
                data-testid="resources-hero-video"
                className="absolute inset-0 w-full h-full object-cover"
                src={RESOURCES_HERO_VIDEO}
                poster={RESOURCES_HERO_IMAGE}
                autoPlay
                muted
                loop
                playsInline
                aria-label="David teaching and working with worship teams"
              />
            ) : (
              <img
                src={RESOURCES_HERO_IMAGE}
                alt="David Rosenblum speaking into a microphone on stage"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            <div className="absolute inset-0 bg-ink/60" aria-hidden="true" />
            <div className="relative p-8 md:p-14 max-w-3xl">
              <h3 className="font-spartan font-extrabold uppercase display-type leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
                People Over Platforms.
                <span className="block mt-2">Presence Over Performance.</span>
              </h3>
              <p className="mt-7 text-base md:text-lg leading-relaxed text-paper/85">
                Worship ministry is about more than building a great set. It’s about building
                healthy people, healthy teams, and a culture that can carry what God wants to do.
              </p>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-paper/70">
                David speaks, teaches, and works with worship leaders and teams to strengthen
                culture, navigate ministry challenges, develop people, and rediscover the heart
                behind what happens on the platform.
              </p>
              <a href="#invite" data-testid="resources-hero-invite-button" className={`${ctaCls} mt-9 !bg-paper !text-ink hover:!bg-sand`}>
                Invite David
              </a>
            </div>
          </div>
        </Reveal>

        {/* ── 2 · Featured teaching / workshop ─────────────── */}
        <div className="mt-16 md:mt-24 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Reveal delay={0.05}>
            <article data-testid="tabernacle-card" className="border border-ink/15 bg-paper h-full flex flex-col">
              <div className="aspect-[16/10] overflow-hidden bg-ink">
                <img
                  src={TABERNACLE_IMAGE}
                  alt="David leading worship at the keyboard"
                  loading="lazy"
                  className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-700"
                />
              </div>
              <div className="p-8 md:p-10 flex flex-col grow">
                <p className={labelCls}>Worship Philosophy</p>
                <h3 className="mt-3 font-spartan font-extrabold uppercase display-type text-3xl md:text-4xl leading-[0.95]">
                  The Tabernacle Approach
                </h3>
                <p className="mt-2 text-sm font-medium text-charcoal/70">
                  From the Outer Courts to the Holy of Holies
                </p>
                <p className="mt-5 text-sm md:text-base leading-relaxed text-charcoal/90">
                  What if the way we lead worship is more than a collection of songs? David
                  teaches a Tabernacle-shaped philosophy of worship that helps leaders think
                  intentionally about movement, participation, intimacy, and encounter —
                  creating worship experiences that lead people somewhere rather than simply
                  filling a setlist.
                </p>
                <p className="mt-5 text-xs tracking-[0.18em] uppercase text-fog">
                  Speaking • Workshops • Worship Team Development
                </p>
                <a href="#invite" data-testid="tabernacle-invite-button" className={`${ctaCls} mt-7 w-fit`}>
                  Invite David to teach this
                </a>
              </div>
            </article>
          </Reveal>

          <Reveal delay={0.12}>
            <article data-testid="habits-card" className="border border-ink/15 bg-paper h-full flex flex-col">
              <div className="aspect-[16/10] bg-sand/60 flex items-center justify-center py-8 overflow-hidden">
                <HabitsCover />
              </div>
              <div className="p-8 md:p-10 flex flex-col grow">
                <p className={labelCls}>Team Workshop</p>
                <h3 className="mt-3 font-spartan font-extrabold uppercase display-type text-3xl md:text-4xl leading-[0.95]">
                  6 Healthy Habits of High-Level Worship Teams
                </h3>
                <p className="mt-5 text-sm md:text-base leading-relaxed text-charcoal/90">
                  Talent can build a platform. Health determines whether you can stay there.
                  This practical workshop explores six habits that help worship ministries build
                  stronger teams, healthier relationships, greater trust, better communication,
                  and a culture where people can flourish.
                </p>
                <p className="mt-5 text-sm md:text-base leading-relaxed text-charcoal/90">
                  Because the strongest worship teams aren’t simply the most talented. They’re
                  healthy.
                </p>
                <a href="#invite" data-testid="habits-invite-button" className={`${ctaCls} mt-7 w-fit`}>
                  Bring this workshop to your team
                </a>
              </div>
            </article>
          </Reveal>
        </div>

        {/* ── 3 · Leadership / coaching teaser ─────────────── */}
        <div className="mt-16 md:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <Reveal className="lg:col-span-5" delay={0.05}>
            <div className="relative">
              <div className="absolute -top-4 -right-4 w-full h-full border border-ink/25" aria-hidden="true" />
              <img
                src={COACHING_IMAGE}
                alt="David talking with a worship leader at the keys"
                data-testid="coaching-image"
                loading="lazy"
                className="relative w-full aspect-[4/5] object-cover"
              />
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <h3 className="font-spartan font-extrabold uppercase display-type text-3xl md:text-5xl leading-[0.95] max-w-xl">
                For worship leaders in the middle of it
              </h3>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-7 text-base md:text-lg leading-relaxed text-charcoal/90 max-w-2xl">
                Ministry has mountains. Sometimes the challenge is musical. Sometimes it’s
                culture. Sometimes it’s navigating personalities, difficult conversations,
                leadership transitions, expectations, or figuring out how to lead the people God
                has actually given you instead of wishing you had a different team.
              </p>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-charcoal/80 max-w-2xl">
                David loves helping worship leaders work through the real-life stuff behind the
                platform — asking better questions, finding clarity, and figuring out the next
                faithful step forward.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <blockquote
                data-testid="coaching-pullquote"
                className="mt-9 border-l-2 border-ink pl-6 md:pl-8 max-w-xl"
              >
                <p className="font-spartan font-bold text-xl md:text-2xl leading-snug tracking-tight">
                  Sometimes you don’t need another conference session. You need a trusted voice
                  for the wilderness and the next mountain.
                </p>
              </blockquote>
            </Reveal>
            <Reveal delay={0.25}>
              <a href="#invite" data-testid="coaching-invite-button" className={`${ctaCls} mt-9`}>
                Start a conversation
              </a>
            </Reveal>
          </div>
        </div>

        {/* ── 5 · What leaders are saying ────────────────── */}
        <Testimonials />
      </div>

      {/* ── 4 · People Over Platforms manifesto ──────────── */}
      <div className="mt-16 md:mt-24 -mx-6 md:-mx-12">
        <div data-testid="manifesto" className="relative overflow-hidden bg-ink text-paper">
          <img
            src={MANIFESTO_IMAGE}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-ink/55" aria-hidden="true" />
          <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36">
            <Reveal>
              <p className="font-spartan font-extrabold uppercase display-type leading-[0.88] text-[16vw] sm:text-[11vw] lg:text-[8.5rem]">
                People
                <span className="block">Over</span>
                <span className="block">Platforms</span>
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-10 text-base md:text-lg leading-relaxed text-paper/85 max-w-2xl">
                Great worship ministry isn’t ultimately measured by how impressive the platform
                becomes, but by what happens in the people entrusted to it.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <ul data-testid="manifesto-statements" className="mt-12 space-y-2">
                {["Build people.", "Develop leaders.", "Create healthy culture.", "Pursue the presence of God."].map(
                  (line) => (
                    <li
                      key={line}
                      className="font-spartan font-extrabold uppercase tracking-tight text-2xl sm:text-3xl md:text-4xl"
                    >
                      {line}
                    </li>
                  )
                )}
              </ul>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-12 text-sm md:text-base text-paper/70 max-w-xl leading-relaxed">
                The platform should serve the people. Never the other way around.
              </p>
            </Reveal>
          </div>
        </div>
      </div>

      {/* ── 5 · Podcasts / conversations ─────────────────── */}
      <div className="max-w-7xl mx-auto">
        <div className="mt-16 md:mt-24">
          <Reveal>
            <p className={labelCls}>Join the conversation</p>
            <h3 className="mt-3 font-spartan font-extrabold uppercase display-type text-3xl md:text-5xl leading-[0.95]">
              Podcasts & Conversations
            </h3>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-charcoal/90 max-w-2xl">
              Listen to David talk worship, leadership, ministry culture, team development, and
              what it means to lead people well.
            </p>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PODCASTS.length === 0
              ? [0, 1, 2].map((i) => (
                  <Reveal key={i} delay={0.08 + i * 0.05}>
                    <div
                      data-testid={`podcast-placeholder-${i}`}
                      className="border border-dashed border-ink/30 p-8 flex flex-col items-start gap-4 min-h-[220px] justify-center"
                    >
                      <Mic size={22} className="text-fog" aria-hidden="true" />
                      <p className="font-spartan font-bold text-lg leading-tight">
                        Podcast appearance
                      </p>
                      <p className="text-xs text-fog leading-relaxed">
                        Artwork, podcast name, episode title, and link — add them in content.js.
                      </p>
                    </div>
                  </Reveal>
                ))
              : PODCASTS.map((podcast, i) => (
                  <Reveal key={podcast.name + i} delay={0.08 + i * 0.05}>
                    <article data-testid={`podcast-card-${i}`} className="group border border-ink/15 bg-paper overflow-hidden flex flex-col">
                      <div className="aspect-square overflow-hidden bg-ink flex items-center justify-center">
                        <img
                          src={podcast.artwork}
                          alt={`${podcast.name} artwork`}
                          loading="lazy"
                          className="w-full h-full object-contain group-hover:scale-[1.03] transition-transform duration-700"
                        />
                      </div>
                      <div className="p-5 md:p-6 flex flex-col grow">
                        <p className="text-[10px] font-semibold tracking-[0.25em] uppercase text-fog">
                          {podcast.name}
                        </p>
                        <h4 className="mt-1.5 font-spartan font-bold text-lg leading-tight">
                          {podcast.episode}
                        </h4>
                        {podcast.descriptor && (
                          <p className="mt-2 text-xs text-fog leading-relaxed">{podcast.descriptor}</p>
                        )}
                        <a
                          href={podcast.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`podcast-listen-${i}`}
                          className="mt-5 inline-flex w-fit items-center gap-2 bg-ink text-paper text-[11px] font-semibold tracking-[0.22em] uppercase px-6 py-3 rounded-full hover:bg-charcoal transition-colors duration-300"
                        >
                          Listen <ArrowUpRight size={14} aria-hidden="true" />
                        </a>
                      </div>
                    </article>
                  </Reveal>
                ))}
          </div>
        </div>

        {/* ── 6 · Final CTA ──────────────────────────────── */}
        <Reveal className="mt-16 md:mt-24">
          <div data-testid="resources-final-cta" className="border-t border-ink/15 pt-14 md:pt-20">
            <h3 className="font-spartan font-extrabold uppercase display-type leading-[0.95] text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
              Let’s help your team climb the next mountain.
            </h3>
            <p className="mt-7 text-base md:text-lg leading-relaxed text-charcoal/90 max-w-2xl">
              Whether it’s a worship workshop, leadership conversation, team development session,
              conference, retreat, or Sunday gathering, David would love to hear what your team
              is walking through and explore how he can serve.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <a href="#invite" data-testid="resources-final-invite-button" className={ctaCls}>
                Invite David
              </a>
              <a
                href="#invite"
                data-testid="resources-final-conversation-link"
                className="inline-flex items-center gap-3 text-xs font-semibold tracking-[0.22em] uppercase border-b border-ink pb-1 hover:gap-5 transition-[gap] duration-300"
              >
                Start a conversation <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
